// @flow

import React from 'react';

import { GLOBAL_APP_COUNT } from '@zapier/constants';
import { Link } from '@zapier/common-components';

import type { ActionInputType, TiersMapType } from 'app/developer-v3/types';
import type { StringMap } from 'app/common/types';

export const rootKey = 'developerV3';

export const BASE_APP_URL = '/app/developer';
export const BASE_API_URL = '/api/platform/cli/apps';
export const BASE_V1_API_URL = '/api/developer/v1';
export const BASE_DOC_URL_VB = 'https://platform.zapier.com';
export const BASE_DOC_URL_CLI = 'https://zapier.github.io/zapier-platform';
export const DOCUMENTATION_URL = '/developer/documentation/v2';
export const BASE_APP_URL_LEGACY = '/developer/builder';
export const DEVELOPER_CLI_GUIDE_URL = '/developer/start';

export const KEY_SUFFIX = 'CLIAPI'; // matches KEY_APPEND in developer_cli/constants.py

export const ALL_VERSIONS_TEXT = 'All';
export const AUDIT_LOGS_LIMIT = 50;

export const NEW_APP_DEFINITION = {
  version: '1.0.0',
};

export const AUTH_SCHEMES = [
  {
    value: 'custom',
    label: 'API Key',
    helpText: (
      <span>
        Use API Key authentication type if you simply need to collect some
        information from your users and then include that information, as it was
        entered by the user, when you make an API request.{' '}
        <Link href="https://platform.zapier.com/docs/apikey" target="_blank">
          Learn more
        </Link>
        .
      </span>
    ),
  },
  {
    value: 'oauth2',
    label: 'OAuth v2',
    helpText: (
      <span>
        Use the OAuth 2 authentication type if your API supports{' '}
        <Link
          href="https://tools.ietf.org/html/rfc6749#section-1.3.1"
          target="_blank"
        >
          OAuth 2 "Authorization Code" grant
        </Link>
        . When setting up a Zap, your user's browser will be redirected to your
        site where you can authenticate them. Your OAuth implementation will
        then return an access token that your Zapier integration will use to
        authorize requests to your API. If your API uses one of the other OAuth
        2 grant types, Session auth or API Key authentication will be a better
        fit.{' '}
        <Link href="https://platform.zapier.com/docs/oauth" target="_blank">
          Learn more
        </Link>
        .
      </span>
    ),
  },
  {
    value: 'session',
    label: 'Session Auth',
    helpText: (
      <span>
        Use the session authentication type if you need to collect some
        information from your users, for example a user name and password, and
        then make a request to your API to exchange that information for a token
        or session key, which you will use to authorize subsequent API requests.
        The Zapier platform will handle refreshing this token automatically each
        time it expires.{' '}
        <Link href="https://platform.zapier.com/docs/session" target="_blank">
          Learn more
        </Link>
        .
      </span>
    ),
  },
  {
    value: 'basic',
    label: 'Basic Auth',
    helpText: (
      <span>
        Use the basic authentication type if your API relies on the{' '}
        <Link href="https://tools.ietf.org/html/rfc7617" target="_blank">
          HTTP “Basic” Authentication standard
        </Link>
        . When your user sets up a new Zap, Zapier will prompt them for a
        username and password, then automatically add the appropriate encoded
        authorization headers to your API requests for you.{' '}
        <Link href="https://platform.zapier.com/docs/basic" target="_blank">
          Learn more
        </Link>
        .
      </span>
    ),
  },
  {
    value: 'digest',
    label: 'Digest Auth',
    helpText: (
      <span>
        Use the digest authentication type if your API uses the{' '}
        <Link href="https://tools.ietf.org/html/rfc2617#page-6" target="_blank">
          HTTP Digest Access Authentication standard
        </Link>{' '}
        to authenticate requests. When your user sets up a new Zap, Zapier will
        prompt them for a username and password, handle the cryptographic
        interaction with your server, and automatically add the appropriate
        encoded authorization headers to your API requests for you.{' '}
        <Link href="https://platform.zapier.com/docs/digest" target="_blank">
          Learn more
        </Link>
        .
      </span>
    ),
  },
];

export const REQUEST_METHODS = [
  {
    value: 'GET',
    label: 'GET',
  },
  {
    value: 'PUT',
    label: 'PUT',
  },
  {
    value: 'POST',
    label: 'POST',
  },
  {
    value: 'PATCH',
    label: 'PATCH',
  },
  {
    value: 'DELETE',
    label: 'DELETE',
  },
  {
    value: 'HEAD',
    label: 'HEAD',
  },
];

export const WRITE_METHODS = ['POST', 'PATCH', 'PUT'];

export const TRACKING_EVENTS = {
  AUTH_CONFIG_STARTED:
    'Track: Visual Builder - Authentication configuration started',
  NEW_ACTION_STARTED: 'Track: Visual Builder - Action configuration started',
  NEW_TRIGGER_STARTED: 'Track: Visual Builder - Trigger configuration started',
  FAILED_MERGE: 'Track: Failed UI Merge',
  SUCCESSFUL_MERGE: 'Track: Successful UI Merge',
};

export const SIDEBAR_VERSION_DROPDOWN_VALUES = {
  ALL: 'all',
  PROMOTE: 'promote',
  DEPRECATE: 'deprecate',
  MIGRATE: 'migrate',
  CLONE: 'clone',
};

export const FIELD_KEYS = {
  altersDynamicFields: 'altersDynamicFields',
  canPaginate: 'canPaginate',
  choices: 'choices',
  computed: 'computed',
  default: 'default',
  dynamic: 'dynamic',
  event: 'event',
  helpText: 'helpText',
  hookType: 'hookType',
  inputFieldsUrl: 'inputFieldsUrl',
  inputFormat: 'inputFormat',
  key: 'key',
  label: 'label',
  list: 'list',
  options: 'options',
  outputFieldsUrl: 'outputFieldsUrl',
  perform: 'perform',
  performList: 'performList',
  performSubscribe: 'performSubscribe',
  performUnsubscribe: 'performUnsubscribe',
  placeholder: 'placeholder',
  resourceUrl: 'resourceUrl',
  required: 'required',
  source: 'source',
  type: 'type',
  url: 'url',
};

export const BASIC_FIELD_TYPE_LABELS = {
  string: 'String',
  text: 'Text',
  integer: 'Integer',
  number: 'Number',
  boolean: 'Boolean',
  datetime: 'DateTime',
  password: 'Password',
};

export const INPUT_FIELD_TYPE_LABELS = {
  ...BASIC_FIELD_TYPE_LABELS,
  dict: 'Dictionary',
  copy:
    'Copy (Shows the Help Text field copy in a display-only formatted note, with no input field.)',
};

export const FIELD_TYPES: $Keys<typeof BASIC_FIELD_TYPE_LABELS>[] = Object.keys(
  BASIC_FIELD_TYPE_LABELS
);

export const INPUT_FIELDS_SELECTOR = ['operation', 'inputFields'];

export const NEW_SERVICE_FIELD = '__new__';

export const LOGS_LOADING_PHRASES = [
  'Fetching logs',
  'Still fetching logs, this might take a little bit',
  `Sorry about that, hopefully this won't take much longer`,
];

export const ACTION_TYPES = {
  creates: 'creates',
  searches: 'searches',
};

export const BASE_SCHEMA_TYPES = {
  ...ACTION_TYPES,
  triggers: 'triggers',
};
export const SERVICE_TYPES = {
  ...BASE_SCHEMA_TYPES,
  searchOrCreates: 'searchOrCreates',
  authentication: 'authentication',
};

export const SERVICE_ROUTES = {
  [SERVICE_TYPES.triggers]: SERVICE_TYPES.triggers,
  actions: 'actions',
};

export const APP_ACTIONS: $Keys<typeof ACTION_TYPES>[] = Object.keys(
  ACTION_TYPES
);

export const DEFAULT_INPUT_FUNCTION = `// Configure a request to an endpoint of your api that
// returns custom field meta data for the authenticated
// user.  Don't forget to congigure authentication!

const options = {
  url: 'https://api.example.com/custom_field_meta_data',
  method: 'GET',
  headers: {
    'Accept': 'application/json'
  },
  params: {

  }
}

return z.request(options)
  .then((response) => {
    response.throwForStatus();
    const results = z.JSON.parse(response.content);

    // modify your api response to return an array of Field objects
    // see https://github.com/zapier/zapier-platform/blob/master/packages/schema/docs/build/schema.md#fieldschema
    // for schema definition.

    return results;
  });
`;

export const APP_INTENTION = {
  global: 'global',
  private: 'private',
  emptyString: '',
};

export const BACKEND_APP_INTENTION = {
  ...APP_INTENTION,
  evaluating: 'evaluating',
};

export const visibilityValues = {
  IMPORTANT: 'important',
  HIDDEN: 'hidden',
  NEITHER: 'regular',
};

const VISIBILITY_CHOICES: Array<StringMap> = [
  {
    value: visibilityValues.IMPORTANT,
    label: 'Important (Displayed above the fold in the Zap Editor)',
  },
  { value: visibilityValues.HIDDEN, label: 'Hidden (Not shown to users)' },
  { value: visibilityValues.NEITHER, label: 'None (Displayed below the fold)' },
].map(({ value, label }) => ({
  value,
  label,
  key: value,
}));

export const VISIBILITY_CHOICES_FIELD = {
  type: 'single-line-string',
  key: 'visibility',
  label: 'Visibility Options',
  choices: VISIBILITY_CHOICES,
  hasSearch: false,
};
// TODO: Not sure about how we'll store this info
export const BUNDLE_INFO = [
  {
    label: 'authData',
    help: '',
    data: [{ label: 'api_key', help: '' }],
  },
  {
    label: 'inputData',
    help: '',
    data: [],
  },
  {
    label: 'inputRawData',
    help: '',
    data: [],
  },
  {
    label: 'meta',
    help: '',
    data: [],
  },
  {
    label: 'rawRequest',
    help: '',
    data: [],
  },
  {
    label: 'cleanedRequest',
    help: '',
    data: [],
  },
];

export const BASE_BUNDLE_META = [
  {
    key: 'isLoadingSample',
    help: 'If true, this run was initiated manually via the Zap Editor',
  },
  {
    key: 'isFillingDynamicDropdown',
    help:
      'If true, this poll is being used to populate a dynamic dropdown. You only need to return the fields you specified (such as id and name), though returning everything is fine too',
  },
  {
    key: 'isPopulatingDedupe',
    help:
      'If true, the results of this poll will be used to initialize the deduplication list rather than trigger a zap. You should grab as many items as possible. See also: deduplication',
  },
  {
    key: 'limit',
    help:
      'The number of items you should fetch. -1 indicates there’s no limit. Build this into your calls insofar as you are able',
  },
  {
    key: 'page',
    help:
      'Used in paging to uniquely identify which page of results should be returned',
  },
  {
    key: 'isTestingAuth',
    help:
      '(legacy property) If true, the poll was triggered by a user testing their account (via clicking "test" or during setup). We use this data to populate the auth label, but it’s mostly used to verify we made a successful authenticated request',
  },
];

export const BASIC_AUTH_FIELDS = [
  {
    key: 'username',
    help: '',
  },
  {
    key: 'password',
    help: '',
  },
];

export const OAUTH2_DATA = [
  {
    key: 'access_token',
  },
  {
    key: 'state',
  },
  {
    key: 'redirect_uri',
  },
  {
    key: 'code',
  },
];

export const ACTION_INPUT_TYPES = Object.freeze({
  INPUT: 'default',
  DYNAMIC: 'dynamic',
  GROUP: 'group',
});

export const ACTION_INPUT_ROUTES = Object.freeze({
  [ACTION_INPUT_TYPES.INPUT]: '',
  [ACTION_INPUT_TYPES.DYNAMIC]: 'dynamic',
  [ACTION_INPUT_TYPES.GROUP]: 'line-item',
});

export const ACTION_INPUT_TYPE_TEXTS = Object.freeze({
  [ACTION_INPUT_TYPES.INPUT]: 'Input Field',
  [ACTION_INPUT_TYPES.DYNAMIC]: 'Dynamic Field',
  [ACTION_INPUT_TYPES.GROUP]: 'Line Item Group',
});

export const PLACEHOLDERS_FIELDS: {
  [ActionInputType]: Object,
} = {
  [ACTION_INPUT_TYPES.INPUT]: {
    create: () => ({
      key: NEW_SERVICE_FIELD,
      label: '',
      type: 'string',
    }),
  },
  [ACTION_INPUT_TYPES.DYNAMIC]: {
    create: () => ({
      source: DEFAULT_INPUT_FUNCTION,
    }),
  },
  [ACTION_INPUT_TYPES.GROUP]: {
    create: () => ({
      key: NEW_SERVICE_FIELD,
      children: [],
    }),
  },
};

export const FORM_TYPES = Object.freeze({
  addCollaborator: 'addCollaboratorCli',
  addInvitee: 'addInviteeCli',
  editApp: 'metaCli',
  newApp: 'create',
});

export const PARTNER_TIERS = [
  {
    key: 'BETA',
    label: 'Beta',
    next: 'BRONZE',
    prev: null,
    perks: [],
  },
  {
    key: 'BRONZE',
    label: 'Bronze',
    next: 'SILVER',
    prev: 'BETA',
    perks: [
      `Buzz-building Launch Campaign`,
      `Access to Integration Health Data`,
      `Dedicated Page in Zapier's App Directory`,
      `"Best Of" Roundup Posts on the Zapier Blog`,
      `Exposure on Zapier's ${GLOBAL_APP_COUNT}+ Partner Sites`,
    ],
  },
  {
    key: 'SILVER',
    label: 'Silver',
    next: 'GOLD',
    prev: 'BRONZE',
    perks: [
      `Get Leads from Zapier's App Directory`,
      `Product Overview on Zapier's App Directory`,
      `Integration Announcements on the Zapier Product Blog`,
    ],
  },
  {
    key: 'GOLD',
    label: 'Gold',
    next: 'PLATINUM',
    prev: 'SILVER',
    perks: [
      `Case Study on the Zapier Blog`,
      `Participation in Promotional Launches`,
    ],
  },
  {
    key: 'PLATINUM',
    label: 'Platinum',
    next: null,
    prev: 'GOLD',
    perks: [
      `Exclusive Dedicated Email to Your Zapier Users`,
      `Quarterly Business Reviews with the Zapier Team`,
      `Hands-on Training`,
    ],
  },
];

export const HEALTH_SCORE_MAP = {
  NA: 'Not Enough Data',
  POOR: 'Poor',
  HEALTHY: 'Healthy',
  EXCEPTIONAL: 'Exceptional',
};

export const TIERS_MAP: TiersMapType = PARTNER_TIERS.reduce(
  (map, tier) => ({ ...map, [tier.key]: tier }),
  {}
);

const HEALTH_MESSAGES = {
  untested: `You'll need at least 20 users before we can calculate your Health Score.`,
  poorMove: 'Get to healthy or above to move to next level.',
  poorMaintain: 'Get to healthy or above to maintain your current level.',
  congratsMove:
    'Congrats! Keep your health score at healthy or above to move to the next level.',
  congratsMaintain:
    'Congrats! Your health score qualifies you to stay in Platinum.',
};

export const HEALTH_TIER_MESSAGES = {
  BETA: {
    NA: HEALTH_MESSAGES.untested,
    POOR: HEALTH_MESSAGES.poorMove,
    HEALTHY: HEALTH_MESSAGES.congratsMove,
    EXCEPTIONAL: HEALTH_MESSAGES.congratsMove,
  },
  BRONZE: {
    NA: HEALTH_MESSAGES.untested,
    POOR: HEALTH_MESSAGES.poorMove,
    HEALTHY: HEALTH_MESSAGES.congratsMove,
    EXCEPTIONAL: HEALTH_MESSAGES.congratsMove,
  },
  SILVER: {
    NA: HEALTH_MESSAGES.untested,
    POOR: HEALTH_MESSAGES.poorMaintain,
    HEALTHY: HEALTH_MESSAGES.congratsMove,
    EXCEPTIONAL: HEALTH_MESSAGES.congratsMove,
  },
  GOLD: {
    NA: HEALTH_MESSAGES.untested,
    POOR: HEALTH_MESSAGES.poorMaintain,
    HEALTHY: HEALTH_MESSAGES.congratsMove,
    EXCEPTIONAL: HEALTH_MESSAGES.congratsMove,
  },
  PLATINUM: {
    NA: HEALTH_MESSAGES.untested,
    POOR: HEALTH_MESSAGES.poorMaintain,
    HEALTHY: HEALTH_MESSAGES.congratsMaintain,
    EXCEPTIONAL: HEALTH_MESSAGES.congratsMaintain,
  },
};

export const STREAM_TYPES = Object.freeze({
  COMMENTS: 'comments',
  EVENTS: 'events',
});

export const DEV_BREAK_POINTS = {
  default: '756px',
  inputTable: '512px',
};

export const APP_STATUS_INVITE_ONLY = {
  name: 'inviteOnly',
  display: 'Invite-Only',
  published: false,
};

export const APP_STATUSES = Object.freeze({
  private: {
    name: 'private',
    display: 'Private',
    lifecycle: 'build',
    published: false,
  },
  pending: {
    name: 'pending',
    display: 'Pending',
    lifecycle: 'review',
    published: false,
  },
  beta: {
    name: 'beta',
    display: 'Public',
    lifecycle: 'beta',
    published: true,
  },
  public: {
    name: 'public',
    display: 'Public',
    lifecycle: 'partnered',
    published: true,
  },
});

export const PUBLISHING_STATUSES = Object.freeze({
  private: 'build',
  pending: 'review',
  beta: 'beta',
  public: 'partnered',
});

const timesToCompletePublishingStep = {
  build: 'About 2 business days',
  pending: 'About 5 business days',
};

type PublishingStep = {
  label: $Values<typeof PUBLISHING_STATUSES>,
  status: $Keys<typeof PUBLISHING_STATUSES>,
  timeToComplete?: string,
  order: number,
  next: {
    label: $Values<typeof PUBLISHING_STATUSES>,
    status: $Keys<typeof PUBLISHING_STATUSES>,
  },
};

export const PUBLISHING_STEPS = (Object.entries(
  PUBLISHING_STATUSES
): any).map<PublishingStep>(([status, label], order, arr) => {
  const [nextStatus, nextLabel] = arr[order + 1] || [];
  const timeToComplete = timesToCompletePublishingStep[label];

  return {
    label,
    status,
    order,
    ...(timeToComplete ? { timeToComplete } : {}),
    next: {
      label: nextLabel,
      status: nextStatus,
    },
  };
});

export const MUTATION_TYPES = {
  add: 'add',
  remove: 'remove',
  update: 'update',
};

export const API_CONFIGURATION_DEFAULT_HEADERS = ['Accept', 'Content-Type'];

export const VALIDATION_RESULT_GROUPINGS = {
  activationTasks: 'activationTasks',
  errors: 'errors',
  passes: 'passes',
  warnings: 'warnings',
};

export const INVITE_STATUSES = {
  accepted: 'accepted',
};

export const ROLES = {
  collaborator: 'collaborator',
  subscriber: 'subscriber',
  invitee: 'invitee',
};

export const NEW_ENTITY_IDS = {
  collaborator: 'new-collaborator',
  invitee: 'new-invitee',
};
